import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserAttribute,
  ICognitoUserData,
} from 'amazon-cognito-identity-js';
import Store from '@/store';
import { development, production } from '@/cognito';
import { applyEmailOtpPatch } from '@/cognito/emailOtpPatch';
import { request } from '@/libs/v1/api/client';

applyEmailOtpPatch();

const config = process.env.NODE_ENV === 'production' ? production : development;

export type SignInResult =
  | { kind: 'SUCCESS'; result: any }
  | {
      kind: 'MFA_REQUIRED';
      challengeName: 'SMS_MFA' | 'EMAIL_OTP' | 'EMAIL_MFA';
      challengeParameters?: any;
    }
  | { kind: 'TOTP_SETUP_REQUIRED'; secretCode: string };

export default class Cognito {
  private userPool = new CognitoUserPool({
    UserPoolId: config.UserPoolId,
    ClientId: config.ClientId,
  });
  private region = config.Region;
  private currentUser = null;
  private pendingUser: CognitoUser | null = null;
  private totpSecretCode: string | null = null;

  constructor() {
    this.currentUser = this.userPool.getCurrentUser();
  }

  // サインアップ
  public signUp(username, password) {
    return new Promise((resolve, reject) => {
      this.userPool.signUp(username, password, [], null, (err, result) => {
        if (err) return reject(err);
        return resolve(result);
      });
    });
  }

  // サインアップ時のコード認証
  public confirmation(username, confirmationCode) {
    const userData = { Username: username, Pool: this.userPool };
    const cognitoUser = new CognitoUser(userData);
    return new Promise((resolve, reject) => {
      cognitoUser.confirmRegistration(confirmationCode, true, (err, result) => {
        if (err) return reject(err);
        return resolve(result);
      });
    });
  }

  // サインイン（MFAが有効なら追加チャレンジに遷移）
  public signin(username: string, password: string): Promise<SignInResult> {
    const userData: ICognitoUserData = { Username: username, Pool: this.userPool };
    const cognitoUser = new CognitoUser(userData);
    const authenticationData = { Username: username, Password: password };
    const authenticationDetails = new AuthenticationDetails(authenticationData);
    return new Promise((resolve, reject) => {
      cognitoUser.authenticateUser(authenticationDetails, {
        onSuccess: (result) => {
          this.currentUser = cognitoUser;
          this.pendingUser = null;
          this.totpSecretCode = null;
          return resolve({ kind: 'SUCCESS', result });
        },
        onFailure: (err) => {
          this.pendingUser = null;
          this.totpSecretCode = null;
          return reject(err);
        },
        mfaRequired: (challengeName, challengeParameters) => {
          this.pendingUser = cognitoUser;
          return resolve({
            kind: 'MFA_REQUIRED',
            challengeName: challengeName as 'SMS_MFA' | 'EMAIL_OTP' | 'EMAIL_MFA',
            challengeParameters,
          });
        },
      });
    });
  }

  // MFA（SMS/Email OTP）コード入力でサインインを完了
  public confirmMfa(code: string, mfaType?: 'SMS_MFA' | 'EMAIL_OTP' | 'EMAIL_MFA'): Promise<any> {
    if (!this.pendingUser) return Promise.reject(new Error('No pending MFA challenge'));
    return new Promise((resolve, reject) => {
      this.pendingUser!.sendMFACode(
        code,
        {
          onSuccess: (result) => {
            this.pendingUser = null;
            this.totpSecretCode = null;
            return resolve(result);
          },
          onFailure: (err) => reject(err),
        },
        mfaType
      );
    });
  }

  public getTotpSecretCode(): string | null {
    return this.totpSecretCode;
  }

  // サインアウト
  public signout() {
    if (this.userPool.getCurrentUser()) {
      this.userPool.getCurrentUser().signOut();
      Store.commit('setUser', null);
    }
  }

  // ユーザ情報を取得
  public getCurrentUser() {
    return new Promise((resolve) => {
      const user = this.userPool.getCurrentUser();
      Store.commit('setUser', user);
      return resolve(user);
    });
  }

  // 認証ずみかどうか
  public isAuthenticated() {
    this.currentUser = this.userPool.getCurrentUser();
    return new Promise((resolve, reject) => {
      if (this.currentUser === null) {
        return reject({ msg: 'No current User' });
      }
      this.currentUser.getSession((err, session) => {
        if (err) return reject(err);
        if (!session.isValid()) {
          return reject('No session');
        } else {
          return resolve(session);
        }
      });
    });
  }

  // 属性の取得
  public async getAttribute(key) {
    return new Promise((resolve, reject) => {
      this.currentUser.getUserAttributes((err, result) => {
        if (err) return reject(err);
        for (const record of result) {
          if (record.Name === key) {
            return resolve(record.Value);
          }
        }
      });
    });
  }

  // コードの再送
  public resetCode() {
    return new Promise((resolve, reject) => {
      this.currentUser.getAttributeVerificationCode('email', {
        onSuccess: (result) => {
          console.log('success getAttributeVerificationCode');
          return resolve(result);
        },
        onFailure: (err) => {
          console.log('failed getAttributeVerificationCode: ' + JSON.stringify(err));
          return reject(err);
        },
      });
    });
  }

  // Eメールアドレス変更後 emailを有効可する
  public async verifyAttribute(confirmationCode) {
    console.log(await this.isAuthenticated());
    return new Promise((resolve, reject) => {
      this.currentUser.verifyAttribute('email', confirmationCode, {
        onSuccess: (result) => {
          console.log('email verification success');
          const user = Store.getters.user.userinfo;
          user.email_verified = 'true';
          Store.commit('setUser', user);
          return resolve(result);
        },
        onFailure: (err) => {
          console.log('email verification failed');
          return reject(err);
        },
      });
    });
  }

  // Eメールアドレスの更新
  public updateEmailAddress(email) {
    const attributes = {
      email: email,
      name: email,
    };
    return new Promise((resolve, reject) => {
      this.updateAttributes(attributes)
        .then((result) => {
          const user = Store.getters.user.userinfo;
          user.email_verified = 'false';
          Store.commit('setUser', user);
          return resolve(result);
        })
        .catch((err) => {
          return reject(err);
        });
    });
  }

  // アクティベーション時のパスワード設定（MFAを経由せずAPI経由で設定）
  public async activatePassword(username: string, tempPassword: string, newPassword: string): Promise<void> {
    await request('/v1/api/activation/password', 'post', {
      email: username,
      tempPassword,
      newPassword,
    });
  }

  // パスワード更新
  public async updatePassword(oldPassword, newPassword) {
    await this.isAuthenticated();
    return new Promise((resolve, reject) => {
      this.currentUser.changePassword(oldPassword, newPassword, (err, result) => {
        if (err) return reject(err);
        return resolve(result);
      });
    });
  }

  // パスワード忘れsms送信
  public forgetPassword(username) {
    const userData = { Username: username, Pool: this.userPool };
    const cognitoUser = new CognitoUser(userData);
    console.log('cognitoUser', JSON.stringify(cognitoUser));
    console.log('------')
    return new Promise((resolve, reject) => {
      cognitoUser.forgotPassword({
        onSuccess: (result) => {
          console.log('sms verification success');
          return resolve(result);
        },
        onFailure: (err) => {
          console.log('sms verification failed');
          return reject(err);
        },
      });
    });
  }

  // パスワードリセット
  public resetPassword(username, newPassword, code) {
    const userData = { Username: username, Pool: this.userPool };
    const cognitoUser = new CognitoUser(userData);
    return new Promise((resolve, reject) => {
      cognitoUser.confirmPassword(code, newPassword, {
        onSuccess: () => {
          console.log('password reset success');
          return resolve('success');
        },
        onFailure: (err) => {
          console.log('password reset failed');
          return reject(err);
        },
      });
    });
  }

  // プロフィール更新
  public async updateAttributes(attributes) {
    await this.isAuthenticated();
    const attributeList = [];
    const attribute = { Name: 'email', Value: attributes.email };
    attributeList.push(new CognitoUserAttribute(attribute));
    return new Promise((resolve, reject) => {
      if (this.currentUser === null) {
        return reject(this.currentUser);
      }
      // update attributes
      this.currentUser.updateAttributes(attributeList, (err, result) => {
        if (err) return reject(err);
        const user = Store.getters.user.userinfo;
        Store.commit('setUser', user);
        return resolve(result);
      });
    });
  }
}
